import React, { useEffect, useState } from 'react';
import { apiService } from '../services/apiService';
import { DSInput, DSSelect, DSTextarea } from '../design-system/primitives';

const TOURNAMENT_TITLE = /capital cup|tournament/i;

/**
 * Whether an event is a tournament: it carries the tournament form's details, or (older
 * events) says so in its title. Shared by the hallway sign's Next Tournament card and the
 * Edit Event form, so whatever the sign calls a tournament can have its details edited.
 */
export const isTournament = (event) => {
  const meta = event.event_metadata || {};
  return Boolean(meta.matchplay_url || meta.tournament_fee || meta.checkin_time
    || meta.qualifier_format || meta.finals_format) || TOURNAMENT_TITLE.test(event.title || '');
};

/**
 * The IFPA formats to pick from. A saved format missing from the list (formats not synced
 * yet, or renamed since) stays selectable, so opening a form never silently clears it.
 */
function FormatOptions({ formats, current }) {
  const names = formats.map((format) => format.format_name);
  return (
    <>
      <option value="">Select format...</option>
      {current && !names.includes(current) && <option value={current}>{current}</option>}
      {formats.map((format) => (
        <option key={format.format_id} value={format.format_name}>{format.format_name}</option>
      ))}
    </>
  );
}

/**
 * A tournament's details, all kept in event_metadata: MatchPlay link, check-in, fees, website
 * cost and categories, qualifying and finals format, prizes. Used by the create form
 * (PinballEventForm) and the Edit Event form (EventDetails). `onChange` gets the input's
 * change event; each input's name is its event_metadata key.
 */
function TournamentDetailsFields({ metadata, onChange }) {
  const [formats, setFormats] = useState({ qualifyingFormats: [], finalsFormats: [] });
  const [formatsError, setFormatsError] = useState(false);

  useEffect(() => {
    let live = true;
    apiService.get('/ifpa-formats')
      .then((response) => {
        if (!live) return;
        setFormats({
          qualifyingFormats: response.data.qualifyingFormats || [],
          finalsFormats: response.data.finalsFormats || []
        });
      })
      .catch((err) => {
        console.error('Error fetching IFPA formats:', err);
        if (live) setFormatsError(true);
      });
    return () => { live = false; };
  }, []);

  const value = (key) => metadata?.[key] ?? '';

  return (
    <>
      <div className="form-group">
        <DSInput
          label="MatchPlay Tournament URL"
          type="url"
          name="matchplay_url"
          value={value('matchplay_url')}
          onChange={onChange}
          placeholder="https://app.matchplay.events/tournaments/..."
        />
      </div>

      <div className="form-row">
        <div className="form-group">
          <DSInput
            label="Check-in Time"
            type="text"
            name="checkin_time"
            value={value('checkin_time')}
            onChange={onChange}
            placeholder="e.g., 6:00 PM"
          />
        </div>
        <div className="form-group">
          <DSInput
            label="Tournament Fee"
            type="number"
            step="0.01"
            name="tournament_fee"
            value={value('tournament_fee')}
            onChange={onChange}
            placeholder="10.00"
          />
        </div>
        <div className="form-group">
          <DSInput
            label="Day Pass Fee (non-members)"
            type="number"
            step="0.01"
            name="day_pass_fee"
            value={value('day_pass_fee')}
            onChange={onChange}
            placeholder="15.00"
          />
        </div>
      </div>

      <div className="form-row">
        <div className="form-group">
          <DSInput
            label="Cost shown on the website"
            type="text"
            name="cost"
            value={value('cost')}
            onChange={onChange}
            placeholder="$30"
          />
          <small>The all-in price, e.g. $30 for tournament plus day pass. Leave blank to show the tournament fee.</small>
        </div>
        <div className="form-group">
          <DSInput
            label="Website categories"
            type="text"
            name="wordpress_categories"
            value={value('wordpress_categories')}
            onChange={onChange}
            placeholder="capital-cup, ifpa, tournament"
          />
          <small>Comma separated, as on the website. Pinball is always added.</small>
        </div>
      </div>

      <div className="form-row">
        <div className="form-group">
          <DSInput
            label="Number of Qualifier Rounds"
            type="number"
            name="num_rounds"
            value={value('num_rounds')}
            onChange={onChange}
            placeholder="4"
          />
        </div>
        <div className="form-group">
          <DSSelect label="Qualifier Format" name="qualifier_format" value={value('qualifier_format')} onChange={onChange}>
            <FormatOptions formats={formats.qualifyingFormats} current={value('qualifier_format')} />
          </DSSelect>
        </div>
      </div>

      <div className="form-row">
        <div className="form-group">
          <DSSelect label="Finals Format" name="finals_format" value={value('finals_format')} onChange={onChange}>
            <FormatOptions formats={formats.finalsFormats} current={value('finals_format')} />
          </DSSelect>
        </div>
        <div className="form-group">
          <DSInput
            label="Finals Structure"
            type="text"
            name="finals_structure"
            value={value('finals_structure')}
            onChange={onChange}
            placeholder="e.g., Top 8 make finals"
          />
        </div>
      </div>
      {formatsError && (
        <small>Couldn't load the IFPA format list. Sync formats in Developer Tools.</small>
      )}

      <div className="form-group">
        <DSTextarea
          label="Prize Information"
          name="prize_info"
          value={value('prize_info')}
          onChange={onChange}
          rows="2"
          placeholder="Describe tournament prizes..."
        />
      </div>
    </>
  );
}

export default TournamentDetailsFields;
